import { ERROR_MESSAGES, MAX_EMAIL_LENGTH, MAX_NAME_LENGTH, MAX_PASSWORD_LENGTH, MAX_ROLE_LENGTH } from "./constants";

export const isValidEmail = (email) =>
  /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email);

export const isValidPhone = (phone) => {
  const digits = phone.replace(/[\s\-().+]+/g, '');
  if (!/^\d+$/.test(digits)) return false;
  if (digits.length === 11 && digits.startsWith('1')) return true;
  return digits.length === 10;
};

// requireRole is false when signature boxes were placed by hand: the boxes
// already say where each person signs, so the Client / Attorney role is
// only informational for the extra people.
export const validateRecipients = (recipients, { requireRole = true } = {}) => {
  const recipientErrors = {};
  let hasError = false;

  for (const r of recipients) {
    const individualError = { name: '', email: '', role: '', phone: '' };
    const method = r.delivery_method || 'EMAIL';
    const needsPhone = method === 'SMS' || method === 'BOTH';

    if (!r.name.trim()) {
      individualError.name = ERROR_MESSAGES.REQUIRED_NAME;
      hasError = true;
    } else if (r.name.length > MAX_NAME_LENGTH) {
      individualError.name = ERROR_MESSAGES.NAME_TOO_LONG(MAX_NAME_LENGTH);
      hasError = true;
    }

    if (!r.email.trim()) {
      individualError.email = ERROR_MESSAGES.INDIVIDUAL_EMAIL_REQUIRED;
      hasError = true;
    } else if (r.email.length > MAX_EMAIL_LENGTH) {
      individualError.email = ERROR_MESSAGES.INDIVIDUAL_EMAIL_TOO_LONG(MAX_EMAIL_LENGTH);
      hasError = true;
    } else if (!isValidEmail(r.email)) {
      individualError.email = ERROR_MESSAGES.INDIVIDUAL_EMAIL_INVALID;
      hasError = true;
    }

    if (needsPhone) {
      const phone = (r.phone || '').trim();
      if (!phone) {
        individualError.phone = ERROR_MESSAGES.REQUIRED_PHONE;
        hasError = true;
      } else if (!isValidPhone(phone)) {
        individualError.phone = ERROR_MESSAGES.INVALID_PHONE;
        hasError = true;
      }
    }

    const role = r.role || '';
    if (!role.trim()) {
      if (requireRole) {
        individualError.role = ERROR_MESSAGES.REQUIRED_ROLE;
        hasError = true;
      }
    } else if (role.length > MAX_ROLE_LENGTH) {
      individualError.role = ERROR_MESSAGES.ROLE_TOO_LONG(MAX_ROLE_LENGTH);
      hasError = true;
    }

    recipientErrors[r.id] = individualError;
  }

  return { recipientErrors, hasError };
};

export const validateExternalShare = ({
  email,
  password,
  expiryDate,
  accessList,
  // Folder shares can be created with nobody to email — the link is the
  // delivery. Defaults to false so the document share is untouched.
  emailOptional = false,
}) => {
  const externalShareErrors = {
    email: "",
    password: "",
    expiryDate: "",
  };

  let hasError = false;

  const emailList = email
    .split(",")
    .map((e) => e.trim())
    .filter((e) => e.length > 0);

  const invalidEmails = emailList.filter((e) => !isValidEmail(e));
  const tooLongEmails = emailList.filter((e) => e.length > MAX_EMAIL_LENGTH);

  if (emailList.length === 0) {
    if (!emailOptional) {
      externalShareErrors.email = ERROR_MESSAGES.REQUIRED_EMAIL;
      hasError = true;
    }
  } else if (tooLongEmails.length > 0) {
    externalShareErrors.email = ERROR_MESSAGES.EMAIL_TOO_LONG(MAX_EMAIL_LENGTH);
    hasError = true;
  } else if (invalidEmails.length > 0) {
    externalShareErrors.email = ERROR_MESSAGES.INVALID_EMAIL;
    hasError = true;
  }

  const requiresPasswordAndExpiry = !accessList?.length;

  if (requiresPasswordAndExpiry) {
    // Password is optional; enforce strength only when one is provided.
    if (password.trim()) {
        const strongPasswordRegex =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

      if (!strongPasswordRegex.test(password)) {
        externalShareErrors.password = ERROR_MESSAGES.INVALID_PASSWORD;
        hasError = true;
      } else if (password.length > MAX_PASSWORD_LENGTH) {
        externalShareErrors.password = ERROR_MESSAGES.PASSWORD_TOO_LONG(MAX_PASSWORD_LENGTH);
        hasError = true;
      }
    }

    if (!expiryDate) {
      externalShareErrors.expiryDate = ERROR_MESSAGES.REQUIRED_EXPIRY;
      hasError = true;
    }
  }

  return { externalShareErrors, hasError };
};
